<template>
  <div class="customer-group-list-container customer-group-list-main customer-group-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Customer Group List']" />

    <div class="glass-tab-view glass-tab-view--flush customer-group-list-glass-shell">
      <div class="glass-tab-view__content customer-group-list-tab-content customer-group-list-page-stack">
        <div class="customer-group-list-header-block">
          <div class="customer-group-list-header-row">
            <div class="customer-group-list-header-row__text">
              <h2>Customer Groups</h2>
              <p class="customer-group-list-header-row__sub">Create and manage groups for organizing customers</p>
            </div>
            <div class="customer-group-list-header-actions">
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="customer-group-list-glass-panel customer-group-list-main-card">
          <div class="consultant-glass-table-section customer-group-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table customer-group-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="customer-group-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="groups.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(group, idx) in groups"
                        :key="group.id || idx"
                        class="consultant-glass-table-row customer-group-list-row"
                        :style="{ '--cgl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ group.name }}</td>
                        <td class="customer-group-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger customer-group-list-delete-btn"
                            @click="openDeleteModal(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewCustomerGroup
      :visible="showModal"
      :companyId="companyId"
      @ok="handleCustomerGroupCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Customer Group"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteGroup"
    />
  </div>
</template>

<script>
import '../styles/CustomerGroupList.css';
import AddNewCustomerGroup from './AddNewCustomerGroup.vue';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'CustomerGroupList',
  components: {
    AddNewCustomerGroup,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      groupName: '',
      groups: [],
      loading: false,
      error: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      deleteIdx: null,
      isDeleting: false,
    };
  },
  computed: {
    hasGroups() {
      return this.groups && this.groups.length > 0;
    },
    isLoading() {
      return this.loading || this.isDeleting;
    },
    canDelete() {
      return this.hasGroups && !this.isLoading;
    },
  },
  async mounted() {
    await this.fetchGroups();
  },
  methods: {
    async fetchGroups() {
      this.loading = true;
      this.error = '';
      try {
        this.groups = await CompanyService.getAllCustomerGroups(this.companyId);
        logger.log('Successfully fetched customer groups:', this.groups.length);
      } catch (err) {
        logger.error('Error fetching customer groups:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Customer groups service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view customer groups.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching customer groups. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage =
            'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching customer groups. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching customer groups. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.groupName = '';
    },
    async handleCustomerGroupCreated(createdGroup) {
      this.closeModal();
      await this.fetchGroups();
      const groupCount = this.groups.length;
      this.successMessage = `Customer group "${createdGroup?.name || 'New Customer Group'}" created successfully! You now have ${groupCount} customer group${groupCount !== 1 ? 's' : ''} in your system.`;
      this.showSuccessModal = true;
      logger.log('Customer group created successfully, total groups:', groupCount);
    },
    openDeleteModal(idx) {
      const group = this.groups[idx];
      if (!group || !group.id) {
        this.errorMessage = 'Invalid group selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }

      if (!group.name || group.name.trim() === '') {
        this.errorMessage = 'Cannot delete a group with an empty name.';
        this.showErrorModal = true;
        return;
      }

      this.deleteIdx = idx;
      this.warningMessage = `Are you sure you want to delete the customer group "${group.name}"? This action cannot be undone and may affect customers assigned to this group.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for group:', group.name);
    },

    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
      this.warningMessage = '';
    },

    async confirmDeleteGroup() {
      if (this.deleteIdx !== null) {
        const group = this.groups[this.deleteIdx];
        if (!group || !group.id) return;

        this.isDeleting = true;
        this.showWarningModal = false;

        try {
          logger.log('Attempting to delete customer group:', group.name, 'ID:', group.id);
          await CompanyService.deleteCustomerGroup(group.id);
          this.groups.splice(this.deleteIdx, 1);
          this.error = '';

          const remainingCount = this.groups.length;
          this.successMessage = `Customer group "${group.name}" has been deleted successfully. You now have ${remainingCount} customer group${remainingCount !== 1 ? 's' : ''} remaining.`;
          this.showSuccessModal = true;
          logger.log('Customer group deleted successfully, remaining groups:', remainingCount);
        } catch (err) {
          logger.error('Error deleting customer group:', err);

          const errorText =
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            '';

          if (
            errorText.includes('foreign key constraint') ||
            errorText.includes('violates foreign key') ||
            errorText.includes('referenced by') ||
            errorText.includes('cannot delete') ||
            errorText.includes('constraint') ||
            errorText.includes('customers') ||
            errorText.includes('customerGroupId') ||
            errorText.includes('CustomerGroups')
          ) {
            if (
              errorText.includes('customers') ||
              errorText.includes('customerGroupId') ||
              errorText.includes('CustomerGroups') ||
              errorText.includes('customer')
            ) {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more customers. Please reassign those customers to another group first.`;
            } else {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently being used by other records. Please reassign those records to another group first.`;
            }
          } else if (err.response?.data?.message) {
            this.errorMessage = err.response.data.message;
          } else if (err.response?.data?.error) {
            this.errorMessage = err.response.data.error;
          } else if (err.response?.data?.detail) {
            this.errorMessage = err.response.data.detail;
          } else if (err.response?.status === 409) {
            this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more customers. Please reassign those customers to another group first.`;
          } else if (err.response?.status === 404) {
            this.errorMessage = 'Customer group not found. It may have already been deleted.';
          } else if (err.response?.status === 403) {
            this.errorMessage = 'You do not have permission to delete customer groups.';
          } else if (err.response?.status === 500) {
            const serverErrorText =
              err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
            if (
              serverErrorText.includes('foreign key constraint') ||
              serverErrorText.includes('violates foreign key') ||
              serverErrorText.includes('referenced by') ||
              serverErrorText.includes('customers') ||
              serverErrorText.includes('customerGroupId') ||
              serverErrorText.includes('CustomerGroups')
            ) {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more customers. Please reassign those customers to another group first.`;
            } else {
              this.errorMessage =
                'Internal server error occurred while deleting the customer group. The server encountered an unexpected condition. Please try again in a few minutes.';
            }
          } else if (err.response?.status === 502) {
            this.errorMessage =
              'Bad gateway error. The server received an invalid response from another server. Please try again later.';
          } else if (err.response?.status === 503) {
            this.errorMessage =
              'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
          } else if (err.response?.status === 504) {
            this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
          } else if (err.response?.status >= 500) {
            this.errorMessage = `Server error (${err.response.status}) occurred while deleting the customer group. Please try again later or contact support if the problem persists.`;
          } else if (err.code === 'NETWORK_ERROR' || !err.response) {
            this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
          } else if (err.message) {
            this.errorMessage = err.message;
          } else {
            this.errorMessage = `An unexpected error occurred while deleting "${group.name}". Please try again.`;
          }
          this.showErrorModal = true;
        } finally {
          this.isDeleting = false;
          this.deleteIdx = null;
          this.warningMessage = '';
        }
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>

